import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useLocation } from "react-router";

import { CollapsibleSection } from "@jti/commerce";
import { checkIfPaymentIsPossibleByStatus, useFeatureFlags } from "@jti/commerce/shared";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Loader } from "@jti/core-components/components/Loader";
import { BREAKPOINT_LARGE } from "@jti/fe-common/constants";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui";

import { OrderPageSidebar } from "@checkout-v2/composites/OrderPageSidebar";
import { PageHeader } from "@checkout-v2/composites/PageHeader";
import { OrderItemsSummaryContainer } from "@checkout-v2/features/ItemsSummary/OrderItemsSummaryContainer";
import { usePaymentProviderContext } from "@checkout-v2/features/payment/context/PaymentProviderContext";
import { PaymentMethodsOrderContainer } from "@checkout-v2/features/PaymentMethods/containers/PaymentMethodsOrderContainer";
import { OrderConsentAndPayForm } from "@checkout-v2/forms/ConsentAndPayForm";
import { ConsentAndPaySection } from "@checkout-v2/shared/components/ConsentAndPaySection";
import { ShippingDataOverview } from "@checkout-v2/shared/components/ShippingDataOverview/ShippingDataOverview";
import { TwoColumnLayout } from "@checkout-v2/shared/components/TwoColumnLayout";
import { useCustomerOrder } from "@checkout-v2/shared/hooks/useCustomerOrder";
import { scrollToElementWithHeaderCompensation } from "@checkout-v2/shared/utils/scrollToElementWithHeaderCompensation";

type OrderPageProps = {
  isPaymentFailed?: boolean;
};

export const OrderPage: FC<OrderPageProps> = ({ isPaymentFailed = false }) => {
  const isDesktopView = useMediaQuery({ minWidth: BREAKPOINT_LARGE });
  const dispatch = useDispatch();
  const hasInitializedSectionsRef = useRef(false);
  const shippingSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const orderConfirmationSectionRef = useRef<HTMLDivElement>(null);
  const { __ } = useTranslations();
  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const location = useLocation();
  const [isPaymentMethodPreselected] = useState(Boolean(location.state?.paymentMethodPreselected));
  const [isPaymentFailedAlertVisible, setIsPaymentFailedAlertVisible] = useState(isPaymentFailed);

  const orderQueryState = useCustomerOrder();
  const sectionsState = useSelector((state) => state.checkoutV2.sectionsState);
  const { isPaymentSessionActive, paymentInitiationStatus } = usePaymentProviderContext();
  const isPaymentSubmissionInProgress = isPaymentSessionActive || paymentInitiationStatus === "loading";
  const securedFieldsPayment = useSelector((state) => state.checkoutV2.data?.securedFieldsPayment);

  const isOrderFreeOfCharge = Boolean(orderQueryState.data?.isZeroValueOrder);

  const isPaymentPossible = checkIfPaymentIsPossibleByStatus(orderQueryState.data?.status);

  const onPaymentSectionEdit = () => {
    if (isPaymentSubmissionInProgress) {
      return;
    }

    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "editing",
        orderConfirmationSection: "rested",
      })
    );

    scrollToElementWithHeaderCompensation(paymentSectionRef.current);
  };

  const scrollIntoSection = useCallback(() => {
    if (sectionsState.paymentSection === "active" && shippingSectionRef.current) {
      scrollToElementWithHeaderCompensation(shippingSectionRef.current);
      return;
    }

    if (sectionsState.orderConfirmationSection === "active" && paymentSectionRef.current) {
      scrollToElementWithHeaderCompensation(paymentSectionRef.current);
    }
  }, [sectionsState.orderConfirmationSection, sectionsState.paymentSection]);

  useEffect(() => {
    if (hasInitializedSectionsRef.current) {
      return;
    }

    if (!orderQueryState.isSuccess || !orderQueryState.data) {
      return;
    }

    hasInitializedSectionsRef.current = true;

    if (!isPaymentPossible) {
      dispatch(
        checkoutV2Slice.actions.setSectionsState({
          shippingSection: "completed",
          paymentSection: "rested",
          orderConfirmationSection: "rested",
        })
      );
      return;
    }

    if (isPaymentMethodPreselected && !securedFieldsPayment) {
      dispatch(
        checkoutV2Slice.actions.setSectionsState({
          shippingSection: "completed",
          paymentSection: "completed",
          orderConfirmationSection: "active",
        })
      );
      return;
    }

    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "active",
        orderConfirmationSection: "rested",
      })
    );
  }, [
    dispatch,
    isPaymentFailed,
    orderQueryState.data,
    orderQueryState.isSuccess,
    isPaymentPossible,
    isPaymentMethodPreselected,
    securedFieldsPayment,
  ]);

  useEffect(() => {
    scrollIntoSection();
  }, [scrollIntoSection]);

  useEffect(() => {
    if (sectionsState.paymentSection === "completed") {
      setIsPaymentFailedAlertVisible(false);
    }
  }, [sectionsState.paymentSection]);

  // ensure alert is visible after SPA navigation
  useEffect(() => {
    setIsPaymentFailedAlertVisible(isPaymentFailed);
  }, [isPaymentFailed, location.key]);

  if (orderQueryState.isLoading) {
    return (
      <TwoColumnLayout>
        <TwoColumnLayout.Header>
          <PageHeader pageTitle={__("checkout.title")} />
        </TwoColumnLayout.Header>

        <TwoColumnLayout.Content>
          <Loader />
        </TwoColumnLayout.Content>
      </TwoColumnLayout>
    );
  }

  if (orderQueryState.isError) {
    return (
      <TwoColumnLayout>
        <TwoColumnLayout.Header>
          <PageHeader pageTitle={__("checkout.title")} />
        </TwoColumnLayout.Header>

        <TwoColumnLayout.Content>
          <AlertBanner
            tone="error"
            title={__("checkout.order.error.title")}
            description={__("checkout.order.error.description")}
          />
        </TwoColumnLayout.Content>
      </TwoColumnLayout>
    );
  }

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.Header>
        <PageHeader pageTitle={__("checkout.title")} itemsCount={orderQueryState.data?.items?.length} />
      </TwoColumnLayout.Header>

      <TwoColumnLayout.Content>
        {!isDesktopView && !ffCheckoutV2EnableIteration2 && <OrderItemsSummaryContainer />}

        <CollapsibleSection
          ref={shippingSectionRef}
          title={__("checkout.shipping.section.title")}
          state={sectionsState.shippingSection}
          data-testid="shipping-section"
        >
          {orderQueryState.data?.shippingAddress ? (
            <ShippingDataOverview
              firstName={orderQueryState.data.shippingAddress.firstname}
              lastName={orderQueryState.data.shippingAddress.lastname}
              telephone={orderQueryState.data.shippingAddress.telephone}
              address={orderQueryState.data.shippingAddress}
              deliveryOptionName={orderQueryState.data.shippingMethod.carrierCode}
            />
          ) : (
            <p>{__("checkout.shipping.section.no_address")}</p>
          )}
        </CollapsibleSection>

        <CollapsibleSection
          ref={paymentSectionRef}
          title={__("checkout.payment.section.title")}
          state={sectionsState.paymentSection}
          data-testid="payment-section"
          isEditButtonDisabled={isPaymentMethodPreselected && !securedFieldsPayment}
          onEdit={
            !isOrderFreeOfCharge && isPaymentPossible && !isPaymentSubmissionInProgress
              ? onPaymentSectionEdit
              : undefined
          }
        >
          <PaymentMethodsOrderContainer
            mode={["active", "editing"].includes(sectionsState.paymentSection) && isPaymentPossible ? "edit" : "view"}
            isPaymentFailedAlertVisible={isPaymentFailedAlertVisible}
          />
        </CollapsibleSection>

        {isDesktopView && isPaymentPossible && (
          <CollapsibleSection
            ref={orderConfirmationSectionRef}
            title={__("checkout.order_confirmation.section.title")}
            state={sectionsState.orderConfirmationSection}
            data-testid="order-confirmation-section"
          >
            <ConsentAndPaySection>
              <OrderConsentAndPayForm />
            </ConsentAndPaySection>
          </CollapsibleSection>
        )}
      </TwoColumnLayout.Content>

      <TwoColumnLayout.Sidebar>
        {isDesktopView && <OrderPageSidebar />}

        {!isDesktopView && (
          <CollapsibleSection
            ref={orderConfirmationSectionRef}
            title={__("checkout.order_confirmation.section.title")}
            state={sectionsState.orderConfirmationSection}
            data-testid="order-confirmation-section"
          >
            <OrderPageSidebar />
            {isPaymentPossible && (
              <ConsentAndPaySection>
                <OrderConsentAndPayForm />
              </ConsentAndPaySection>
            )}
          </CollapsibleSection>
        )}
      </TwoColumnLayout.Sidebar>
    </TwoColumnLayout>
  );
};
